import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";
import { FlowArrow } from "../EAKCArchitecture/components/FlowArrow";
import { DataParticle } from "../EAKCArchitecture/components/DataParticle";

// Simulated search results
const VECTOR_RESULTS = [
  { chunk: "src/lib/rag/map-reduce-response.ts", score: 0.96 },
  { chunk: "src/lib/rag/entry-point-tracer.ts", score: 0.93 },
  { chunk: "src/lib/rag/graph-augmented-retrieval.ts", score: 0.91 },
  { chunk: "src/lib/chat/completion.ts", score: 0.87 },
  { chunk: "src/lib/rag/retrieval.ts", score: 0.84 },
];

const BM25_RESULTS = [
  { chunk: "src/lib/rag/reranker.ts", score: 0.90 },
  { chunk: "src/lib/rag/query-rewriter.ts", score: 0.86 },
  { chunk: "src/app/api/v1/chat/completions/route.ts", score: 0.81 },
  { chunk: "src/lib/mcp/tools.ts", score: 0.74 },
  { chunk: "src/lib/rag/embeddings.ts", score: 0.68 },
];

export const Scene4HybridSearch: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  const exitOpacity = interpolate(frame, [330, 360], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Query enters
  const queryAppear = spring({ frame: frame - 10, fps, config: SPRING.gentle });

  // Fork point
  const forkAppear = spring({ frame: frame - 40, fps, config: SPRING.bouncy });

  // Merge point
  const mergeAppear = spring({ frame: frame - 220, fps, config: SPRING.bouncy });

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.06} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 50,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
        }}
      >
        <div
          style={{
            fontSize: 42,
            fontFamily: FONTS.heading,
            fontWeight: 700,
            color: COLORS.textPrimary,
          }}
        >
          Step 3:{" "}
          <span style={{ color: COLORS.accentTeal }}>Hybrid Retrieval</span>
        </div>
        <div
          style={{
            fontSize: 18,
            fontFamily: FONTS.body,
            color: COLORS.textSecondary,
            marginTop: 8,
          }}
        >
          pgvector (semantic) + BM25 (keyword) + title match, fused — then cross-source linking
        </div>
      </div>

      {/* Incoming query — left */}
      <div
        style={{
          position: "absolute",
          left: 80,
          top: 440,
          opacity: interpolate(queryAppear, [0, 1], [0, 1]),
          transform: `translateX(${interpolate(queryAppear, [0, 1], [-30, 0])}px)`,
        }}
      >
        <div
          style={{
            padding: "16px 24px",
            borderRadius: 12,
            background: COLORS.bgCard,
            border: `1px solid ${COLORS.accentBlue}40`,
            fontFamily: FONTS.mono,
            fontSize: 14,
            color: COLORS.textPrimary,
            maxWidth: 200,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 22, marginBottom: 6 }}>💬</div>
          5 sub-queries
        </div>
      </div>

      {/* Fork arrows */}
      <FlowArrow fromX={290} fromY={420} toX={440} toY={260} delay={30} color={COLORS.accentTeal} strokeWidth={2} />
      <FlowArrow fromX={290} fromY={480} toX={440} toY={650} delay={35} color={COLORS.accentYellow} strokeWidth={2} />

      {/* Fork node */}
      <div
        style={{
          position: "absolute",
          left: 290,
          top: 450,
          transform: `translate(-50%, -50%) scale(${interpolate(forkAppear, [0, 1], [0, 1])})`,
          opacity: interpolate(forkAppear, [0, 1], [0, 1]),
          width: 50,
          height: 50,
          borderRadius: "50%",
          background: `${COLORS.bgCard}`,
          border: `2px solid ${COLORS.borderLight}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
        }}
      >
        ⑂
      </div>

      {/* === VECTOR SEARCH — top path === */}
      <div
        style={{
          position: "absolute",
          left: 420,
          top: 155,
          width: 520,
        }}
      >
        {/* Header */}
        <div
          style={{
            opacity: interpolate(
              spring({ frame: frame - 50, fps, config: SPRING.snappy }),
              [0, 1],
              [0, 1],
            ),
            marginBottom: 14,
          }}
        >
          <div
            style={{
              padding: "10px 20px",
              borderRadius: 10,
              background: `${COLORS.accentTeal}12`,
              border: `1.5px solid ${COLORS.accentTeal}40`,
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 18 }}>🔢</span>
            <span
              style={{
                fontFamily: FONTS.mono,
                fontSize: 16,
                fontWeight: 700,
                color: COLORS.accentTeal,
              }}
            >
              Vector Search
            </span>
            <span
              style={{
                fontFamily: FONTS.mono,
                fontSize: 12,
                color: COLORS.textMuted,
                marginLeft: 8,
              }}
            >
              pgvector cosine similarity · weight: 70%
            </span>
          </div>
        </div>

        {/* Results */}
        {VECTOR_RESULTS.map((result, i) => {
          const rAppear = spring({
            frame: frame - 70 - i * 12,
            fps,
            config: SPRING.snappy,
          });

          // Score bar width
          const barWidth = result.score * 100;

          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "8px 14px",
                marginBottom: 6,
                borderRadius: 8,
                background: `${COLORS.bgCard}80`,
                opacity: interpolate(rAppear, [0, 1], [0, 1]),
                transform: `translateX(${interpolate(rAppear, [0, 1], [20, 0])}px)`,
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 12,
                  color: COLORS.textSecondary,
                  minWidth: 240,
                }}
              >
                {result.chunk}
              </span>
              <div
                style={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  background: `${COLORS.accentTeal}15`,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${barWidth}%`,
                    height: "100%",
                    borderRadius: 3,
                    background: COLORS.accentTeal,
                    opacity: 0.7,
                  }}
                />
              </div>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 13,
                  fontWeight: 600,
                  color: COLORS.accentTeal,
                  minWidth: 40,
                  textAlign: "right",
                }}
              >
                {result.score.toFixed(2)}
              </span>
            </div>
          );
        })}
      </div>

      {/* === BM25 SEARCH — bottom path === */}
      <div
        style={{
          position: "absolute",
          left: 420,
          top: 545,
          width: 520,
        }}
      >
        {/* Header */}
        <div
          style={{
            opacity: interpolate(
              spring({ frame: frame - 55, fps, config: SPRING.snappy }),
              [0, 1],
              [0, 1],
            ),
            marginBottom: 14,
          }}
        >
          <div
            style={{
              padding: "10px 20px",
              borderRadius: 10,
              background: `${COLORS.accentYellow}12`,
              border: `1.5px solid ${COLORS.accentYellow}40`,
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 18 }}>📝</span>
            <span
              style={{
                fontFamily: FONTS.mono,
                fontSize: 16,
                fontWeight: 700,
                color: COLORS.accentYellow,
              }}
            >
              BM25 Search
            </span>
            <span
              style={{
                fontFamily: FONTS.mono,
                fontSize: 12,
                color: COLORS.textMuted,
                marginLeft: 8,
              }}
            >
              PostgreSQL tsvector FTS · weight: 30%
            </span>
          </div>
        </div>

        {/* Results */}
        {BM25_RESULTS.map((result, i) => {
          const rAppear = spring({
            frame: frame - 80 - i * 12,
            fps,
            config: SPRING.snappy,
          });

          const barWidth = result.score * 100;

          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "8px 14px",
                marginBottom: 6,
                borderRadius: 8,
                background: `${COLORS.bgCard}80`,
                opacity: interpolate(rAppear, [0, 1], [0, 1]),
                transform: `translateX(${interpolate(rAppear, [0, 1], [20, 0])}px)`,
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 12,
                  color: COLORS.textSecondary,
                  minWidth: 240,
                }}
              >
                {result.chunk}
              </span>
              <div
                style={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  background: `${COLORS.accentYellow}15`,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${barWidth}%`,
                    height: "100%",
                    borderRadius: 3,
                    background: COLORS.accentYellow,
                    opacity: 0.7,
                  }}
                />
              </div>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 13,
                  fontWeight: 600,
                  color: COLORS.accentYellow,
                  minWidth: 40,
                  textAlign: "right",
                }}
              >
                {result.score.toFixed(2)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Merge arrows — converge to right */}
      <FlowArrow fromX={950} fromY={300} toX={1100} toY={440} delay={200} color={COLORS.accentTeal} strokeWidth={2} />
      <FlowArrow fromX={950} fromY={700} toX={1100} toY={480} delay={205} color={COLORS.accentYellow} strokeWidth={2} />

      {frame > 200 && <DataParticle fromX={955} fromY={305} toX={1095} toY={435} delay={210} duration={30} color={COLORS.accentTeal} size={4} count={2} />}
      {frame > 205 && <DataParticle fromX={955} fromY={695} toX={1095} toY={475} delay={215} duration={30} color={COLORS.accentYellow} size={4} count={2} />}

      {/* Merge / Score Fusion box — right */}
      <div
        style={{
          position: "absolute",
          left: 1100,
          top: 460,
          transform: `translate(-50%, -50%) scale(${interpolate(mergeAppear, [0, 1], [0.4, 1])})`,
          opacity: interpolate(mergeAppear, [0, 1], [0, 1]),
          width: 280,
          padding: "24px",
          borderRadius: 16,
          background: `linear-gradient(135deg, ${COLORS.accentTeal}12, ${COLORS.accentYellow}08)`,
          border: `2px solid ${COLORS.borderLight}`,
          textAlign: "center",
          boxShadow: `0 0 30px ${COLORS.accentTeal}10`,
        }}
      >
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 18,
            fontWeight: 700,
            color: COLORS.textPrimary,
            marginBottom: 10,
          }}
        >
          Score Fusion
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 13,
            color: COLORS.textMuted,
            lineHeight: 1.8,
          }}
        >
          <span style={{ color: COLORS.accentTeal }}>0.7</span> × vector_score
          <br />
          + <span style={{ color: COLORS.accentYellow }}>0.3</span> × bm25_score
          <br />
          + <span style={{ color: COLORS.accentBlue }}>title-match</span> boost
        </div>
        <div
          style={{
            marginTop: 14,
            padding: "8px 16px",
            borderRadius: 8,
            background: `${COLORS.accentGreen}15`,
            border: `1px solid ${COLORS.accentGreen}30`,
            fontFamily: FONTS.mono,
            fontSize: 13,
            color: COLORS.accentGreen,
          }}
        >
          → fused pool → cross-source linked
        </div>
      </div>

      {/* Latency */}
      {(() => {
        const latAppear = spring({ frame: frame - 250, fps, config: SPRING.snappy });
        return (
          <div
            style={{
              position: "absolute",
              bottom: 60,
              right: 120,
              opacity: interpolate(latAppear, [0, 1], [0, 1]),
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 32,
                fontWeight: 700,
                color: COLORS.accentTeal,
              }}
            >
              ~150ms
            </div>
            <div
              style={{
                fontFamily: FONTS.body,
                fontSize: 14,
                color: COLORS.textMuted,
                marginTop: 4,
              }}
            >
              Search Latency
            </div>
          </div>
        );
      })()}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
